<template>
	<div class="manual-lines">
		<div v-for="(line, index) in modelValue" :key="line.key" class="manual-line">
			<div class="line-fields">
				<AppTextInput class="line-description" :label="getString('description')" :value="line.description" @changing-text="v => changeLine(index, {description: v})" />
				<AppNumberInput :label="getString('quantity')" :value="line.quantity" :min="0" @changed-value="v => changeLine(index, {quantity: v})" />
				<AppNumberInput :label="priceLabel" :value="line.price" :min="0" double-decimal :symbol="currencyStore.currCurrency.symbol" right-side-symbol @changed-value="v => changeLine(index, {price: v})" />
				<AppSelect class="line-vat" :label="getString('vat_rate')" :values="vatRates.map(rate => rate + '%')" :selected-index="vatRates.indexOf(line.vat)" @selected="i => changeLine(index, {vat: vatRates[i]})" />
				<AppButtonIcon class="line-delete" icon-name="icon-trash-white" prevent-change :title="getString('delete')" :disabled="modelValue.length === 1" @click-icon="removeLine(index)" />
			</div>
			<p v-if="line.quantity > 0 && !isValidQuantity(line.quantity)" class="line-error">{{ getString('manual_invoice_whole_quantity') }}</p>
			<AppSelect
				v-if="withNature && line.vat === 0"
				class="line-nature"
				:label="getString('vat_nature')"
				:values="natureOptions.map(option => option.text)"
				:selected-index="natureIndex(line)"
				@selected="i => changeLine(index, {natura_iva: natureOptions[i].value})" />
		</div>
		<AppButton class="add-line" small outline :text="getString('manual_invoice_add_line')" @click="addLine" />
	</div>
</template>

<script>
import AppButton from "@/shared/components/AppButton.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppNumberInput from "@/shared/components/AppNumberInput.vue";
import AppSelect from "@/shared/components/AppSelect.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import {useEpsonStore, useUserStore} from "@/console-new/stores";
import {useCurrencyStore, useLanguageStore} from "@/shared/stores";
import {doubleFigure} from "@/shared/helpers/dates";

let nextLineKey = 1;

// The checks of PaymentsController::emitManualInvoice and CardsSelfInvoicesController::emitManual, each with its own message
export const INVALID_DOCUMENT_ERRORS = ["no_customer", "no_items", "too_many_items", "invalid_item_description", "invalid_item_quantity", "invalid_item_price", "invalid_vat_rate", "missing_vat_nature", "invalid_invoice_date", "invalid_payment_type", "missing_due_date", "invalid_due_date", "integration_missing", "missing_supplier_data", "invalid_supplier_country", "invalid_supplier_vat", "invalid_reference_numero", "invalid_reference_date"];

/**
 * The local date as the date inputs and the API take it, Y-m-d
 */
export function isoDate(date) {
	return date.getFullYear() + "-" + doubleFigure(date.getMonth() + 1) + "-" + doubleFigure(date.getDate());
}

/**
 * Whole quantities only: Apifattura's PDF and credit notes read them as integers
 */
export function isValidQuantity(quantity) {
	return Number.isInteger(quantity) && quantity > 0;
}

/**
 * A line of a manual invoice, its price in the currency (not cents) as the inputs edit it
 */
export function emptyLine(vat) {
	return {key: nextLineKey++, description: "", quantity: 1, price: 0, vat: vat, natura_iva: null};
}

/**
 * The lines of an invoice typed in the console: description, quantity, unit price and VAT rate, with the VAT nature
 * of a 0% line when withNature
 */
export default {
	setup() {
		const languageStore = useLanguageStore();
		const currencyStore = useCurrencyStore();
		const epsonStore = useEpsonStore();
		const userStore = useUserStore();
		return {getString: languageStore.getString, currencyStore, epsonStore, userStore};
	},
	name: "ManualInvoiceLines",
	components: {AppButton, AppButtonIcon, AppNumberInput, AppSelect, AppTextInput},
	emits: ["update:modelValue"],
	props: {
		modelValue: {type: Array, required: true},
		vatRates: {type: Array, required: true},
		priceLabel: {type: String, required: true},
		withNature: {type: Boolean, default: false},
	},
	computed: {
		// Each regulatory reference of each nature, as the menu categories store it: nature.index
		natureOptions() {
			let country = this.userStore.currBusiness && this.epsonStore.countryVatsNature[this.userStore.currBusiness.country] ? this.userStore.currBusiness.country : "IT";
			let natures = this.epsonStore.countryVatsNature[country];
			return Object.keys(natures).flatMap(nature => natures[nature].map((reg, index) => ({value: nature + "." + index, text: nature + " - " + reg})));
		},
	},
	methods: {
		isValidQuantity,
		natureIndex(line) {
			let index = this.natureOptions.findIndex(option => option.value === line.natura_iva);
			return index >= 0 ? index : null;
		},
		changeLine(index, changes) {
			let lines = this.modelValue.slice();
			lines[index] = Object.assign({}, lines[index], changes);
			this.$emit("update:modelValue", lines);
		},
		removeLine(index) {
			let lines = this.modelValue.slice();
			lines.splice(index, 1);
			this.$emit("update:modelValue", lines);
		},
		addLine() {
			this.$emit("update:modelValue", [...this.modelValue, emptyLine(this.vatRates[0])]);
		},
	},
}
</script>

<style scoped lang="scss">
.manual-lines {
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.manual-line {
	display: flex;
	flex-direction: column;
	gap: 8px;
	padding-bottom: 12px;
	border-bottom: 1px solid var(--color-border-2);
}

.line-fields {
	display: flex;
	flex-wrap: wrap;
	align-items: flex-end;
	gap: 8px;

	.line-description {
		flex: 1 1 200px;
		width: auto;
	}

	.line-vat {
		width: 90px;
		min-width: 90px;
	}

	.line-delete {
		margin-bottom: 4px;
	}
}

.manual-line .line-nature {
	max-width: 100%;
}

.line-error {
	font-size: 0.85em;
	color: var(--color-danger);
}

.add-line {
	align-self: flex-start;
}
</style>
